/**
 * Per-browser opt-in to the server-side menu engine, for trying it in production with one
 * account before it becomes the default.
 *
 *   ?motor=servidor   → this browser builds and swaps menus on the server
 *   ?motor=navegador  → back to the browser generator
 *
 * The choice is remembered in localStorage so it survives reloads and in-app navigation. Storage
 * can be unavailable (private mode, blocked site data); then the opt-in simply does not stick and
 * the browser generator is used, which is the behaviour everyone else has anyway.
 */
const STORAGE_KEY = "thinkeat.menuEngine";
const QUERY_PARAM = "motor";
const SERVER = "servidor";
const BROWSER = "navegador";

function readStored(): string | null {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStored(value: string | null): void {
  try {
    if (value === null) window.localStorage.removeItem(STORAGE_KEY);
    else window.localStorage.setItem(STORAGE_KEY, value);
  } catch {
    // Storage blocked: the opt-in will not persist, and the default engine stays in use.
  }
}

/**
 * Applies `?motor=` if present. HashRouter keeps routes after `#`, so the parameter can sit in
 * either the normal query string or the one inside the hash.
 */
export function applyMenuEngineFromUrl(location: Pick<Location, "search" | "hash"> = window.location): void {
  const fromSearch = new URLSearchParams(location.search).get(QUERY_PARAM);
  const hashQuery = location.hash.includes("?") ? location.hash.slice(location.hash.indexOf("?")) : "";
  const fromHash = new URLSearchParams(hashQuery).get(QUERY_PARAM);
  const requested = fromSearch ?? fromHash;

  if (requested === SERVER) writeStored(SERVER);
  else if (requested === BROWSER) writeStored(null);
}

/** True when this browser has opted in to the server-side engine. */
export function usesServerMenuEngine(): boolean {
  return readStored() === SERVER;
}
